import { useRef, useEffect, useState, useCallback } from "react";
import { IonButton, IonRange } from "@ionic/react";
import { Undo2, Trash2, Check } from "lucide-react";

// Signatures are exported in black ink no matter how they are shown: the
// documents are white paper. On screen the ink follows the theme (white on
// the dark box, black on the light one) so it is always easy to read.
//
// Black here means black. The near-black this used to use (#111827, a slate
// whose blue channel is over twice its red) reads as blue once a thin stroke
// is flattened onto white paper: half a stroke's pixels are antialiased
// edges, so the average lands around rgb(79,85,96) rather than near-black.
const EXPORT_INK = "#000000";
const LIGHT_INK = "#000000";
const DARK_INK = "#ffffff";

const DEFAULT_PEN = 3.2;

// The app toggles body.dark; watch it so an open modal follows a theme change.
export function useDarkMode() {
  const read = () => typeof document !== "undefined" && document.body.classList.contains("dark");
  const [dark, setDark] = useState(read);
  useEffect(() => {
    if (typeof MutationObserver === "undefined") return undefined;
    const mo = new MutationObserver(() => setDark(read()));
    mo.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);
  return dark;
}

// The signature box every modal uses: the page background colour, rounded
// corners, a hairline border, and whatever it holds centred inside.
export function SignatureBox({ height = 150, children, style, className = "", ...rest }) {
  return (
    <div
      className={`sig-box ${className}`.trim()}
      style={{
        position: "relative", width: "100%", height, overflow: "hidden",
        background: "var(--ion-background-color)",
        border: "none",
        boxShadow: "rgba(0,0,0,0.18) 0 4px 24px", // the card shadow used across the app
        borderRadius: 12,
        display: "flex", alignItems: "center", justifyContent: "center",
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

// An uploaded signature shown in the box: dark ink on the light box, and
// inverted on the dark box so the ink reads white there.
export function SignatureImage({ src, dark, alt = "signature" }) {
  return (
    <img
      src={src} alt={alt}
      style={{ maxWidth: "80%", maxHeight: "70%", objectFit: "contain", filter: dark ? "invert(1) hue-rotate(180deg)" : "none" }}
    />
  );
}

/**
 * Canvas signature pad. Draw with mouse, touch, or stylus.
 * Calls onChange(dataUrl | null) with a trimmed, transparent-background PNG
 * in black ink.
 */
export default function SignaturePad({ onChange, height = 150, dark: darkProp }) {
  const canvasRef  = useRef(null);
  const wrapRef    = useRef(null);
  const drawingRef = useRef(false);
  const pointsRef  = useRef([]);     // current stroke
  const strokesRef = useRef([]);     // completed strokes (for undo)

  const autoDark = useDarkMode();
  const dark = darkProp === undefined ? autoDark : !!darkProp; // pages that theme themselves pass it in
  const ink = dark ? DARK_INK : LIGHT_INK;
  const inkRef = useRef(ink);
  inkRef.current = ink;

  const [size, setSize]     = useState(DEFAULT_PEN);
  const [isEmpty, setEmpty] = useState(true);
  const [penDown, setPenDown] = useState(false); // hides the hint as soon as drawing starts

  // ── set up / resize canvas for HiDPI ───────────────────────────────────────
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const dpr = window.devicePixelRatio || 1;
    const cssW = wrap.clientWidth;
    const cssH = height;
    // Inside a modal that is still animating in, the wrapper measures 0 wide.
    // Skip — the ResizeObserver below re-runs this once layout settles.
    if (!cssW) return;

    canvas.width  = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.width  = cssW + "px";
    canvas.style.height = cssH + "px";

    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    return ctx;
  }, [height]);

  // Redraw every stored stroke (after resize / undo / theme change)
  const redraw = useCallback(() => {
    const ctx = setupCanvas();
    if (!ctx) return;
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    for (const stroke of strokesRef.current) {
      drawStroke(ctx, stroke.points, inkRef.current, stroke.size);
    }
  }, [setupCanvas]);

  useEffect(() => { redraw(); }, [redraw, dark]);

  useEffect(() => {
    const onResize = () => redraw();
    window.addEventListener("resize", onResize);
    // Re-size the canvas whenever the wrapper's width actually changes — this
    // covers mounting inside a modal mid-present, when clientWidth is still 0.
    let ro;
    if (typeof ResizeObserver !== "undefined" && wrapRef.current) {
      let lastW = wrapRef.current.clientWidth;
      ro = new ResizeObserver(() => {
        const w = wrapRef.current?.clientWidth || 0;
        if (w !== lastW) { lastW = w; redraw(); }
      });
      ro.observe(wrapRef.current);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
    };
  }, [redraw]);

  // ── stroke rendering with midpoint smoothing ───────────────────────────────
  function drawStroke(ctx, points, strokeColor, strokeSize) {
    if (!points.length) return;
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = strokeSize;
    ctx.beginPath();

    if (points.length < 3) {
      // A tap (the dot on an "i"): a round dot a little bolder than the line
      const p = points[0];
      ctx.arc(p.x, p.y, Math.max(1.5, strokeSize * 0.75), 0, Math.PI * 2);
      ctx.fillStyle = strokeColor;
      ctx.fill();
      return;
    }

    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length - 1; i++) {
      const midX = (points[i].x + points[i + 1].x) / 2;
      const midY = (points[i].y + points[i + 1].y) / 2;
      ctx.quadraticCurveTo(points[i].x, points[i].y, midX, midY);
    }
    const last = points[points.length - 1];
    ctx.lineTo(last.x, last.y);
    ctx.stroke();
  }

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  // ── export trimmed PNG (always black ink) ──────────────────────────────────
  const exportSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || strokesRef.current.length === 0) {
      onChange?.(null);
      return;
    }
    const dpr = window.devicePixelRatio || 1;
    const { width, height: h } = canvas;

    // Re-ink the strokes in black on an offscreen canvas of the same size
    const black = document.createElement("canvas");
    black.width = width;
    black.height = h;
    const bctx = black.getContext("2d");
    bctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    bctx.lineCap = "round";
    bctx.lineJoin = "round";
    for (const stroke of strokesRef.current) drawStroke(bctx, stroke.points, EXPORT_INK, stroke.size);

    // Bounding box of non-transparent pixels
    const data = bctx.getImageData(0, 0, width, h).data;
    let minX = width, minY = h, maxX = 0, maxY = 0, found = false;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < width; x++) {
        if (data[(y * width + x) * 4 + 3] > 8) {
          found = true;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (!found) { onChange?.(null); return; }

    const pad = Math.round(8 * dpr);
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(width - 1, maxX + pad);
    maxY = Math.min(h - 1, maxY + pad);

    const outW = maxX - minX + 1;
    const outH = maxY - minY + 1;

    const out = document.createElement("canvas");
    out.width = outW;
    out.height = outH;
    out.getContext("2d").drawImage(black, minX, minY, outW, outH, 0, 0, outW, outH);

    onChange?.(out.toDataURL("image/png"));
  }, [onChange]);

  // ── pointer handlers ───────────────────────────────────────────────────────
  const handleDown = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    pointsRef.current = [getPos(e)];
    setPenDown(true);
    // Show the touch right away, so a plain tap paints a dot even before
    // the pointer moves or lifts
    const ctx = canvasRef.current.getContext("2d");
    drawStroke(ctx, pointsRef.current, inkRef.current, size);
  };

  const handleMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    pointsRef.current.push(getPos(e));

    // Live render: redraw committed strokes + the in-progress one
    const ctx = canvasRef.current.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvasRef.current.width / dpr, canvasRef.current.height / dpr);
    for (const s of strokesRef.current) drawStroke(ctx, s.points, inkRef.current, s.size);
    drawStroke(ctx, pointsRef.current, inkRef.current, size);
  };

  const handleUp = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    drawingRef.current = false;
    if (pointsRef.current.length) {
      strokesRef.current.push({ points: pointsRef.current, size });
      pointsRef.current = [];
      setEmpty(false);
      redraw(); // paints the committed stroke (a tap's dot included) exactly as stored
      exportSignature();
    }
  };

  const handleUndo = () => {
    strokesRef.current.pop();
    redraw();
    const empty = strokesRef.current.length === 0;
    setEmpty(empty);
    empty ? onChange?.(null) : exportSignature();
  };

  const handleClear = () => {
    strokesRef.current = [];
    pointsRef.current = [];
    redraw();
    setEmpty(true);
    setPenDown(false);
    onChange?.(null);
  };

  return (
    <div className="sig-pad" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* The box */}
      <SignatureBox height={height} style={{ display: "block" }}>
        <div ref={wrapRef} style={{ position: "absolute", inset: 0 }}>
          <canvas
            ref={canvasRef}
            onPointerDown={handleDown}
            onPointerMove={handleMove}
            onPointerUp={handleUp}
            onPointerLeave={handleUp}
            onPointerCancel={handleUp}
            className="sig-canvas"
            style={{ display: "block", cursor: "crosshair", touchAction: "none" }}
          />
          {/* The hint fades out the moment the pen touches the box and comes
              back when the box is cleared */}
          <div
            className="sig-hint"
            style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none", opacity: isEmpty && !penDown ? 1 : 0, transition: "opacity 0.3s ease" }}
            aria-hidden={!(isEmpty && !penDown)}
          >
            <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--ion-color-medium)" }}>Draw your signature here</p>
            <p style={{ margin: "4px 0 0", fontSize: "0.72rem", color: "var(--ion-color-medium)", opacity: 0.7 }}>Use your mouse, finger, or stylus</p>
          </div>
        </div>
      </SignatureBox>

      {/* Pen width — an Ionic slider */}
      <div className="sig-pen" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: "0.75rem", color: "var(--ion-color-medium)", whiteSpace: "nowrap" }}>Pen width</span>
        <IonRange
          aria-label="Pen width"
          min={1} max={7} step={0.5} value={size}
          onIonInput={(e) => {
            const v = Number(e.detail.value);
            setSize(v);
            if (strokesRef.current.length) {
              // an already-drawn signature follows the pen width live
              strokesRef.current = strokesRef.current.map((st) => ({ ...st, size: v }));
              redraw();
              exportSignature();
            }
          }}
          style={{ flex: 1, "--bar-height": "4px", "--knob-size": "20px", padding: "0 4px" }}
        />
        <span style={{ display: "inline-block", width: 22, height: 22, borderRadius: "50%", background: "var(--ion-background-color)", border: "1px solid var(--ion-border-color, rgba(0,0,0,0.12))", position: "relative", flexShrink: 0 }} aria-hidden="true">
          <span style={{ position: "absolute", left: "50%", top: "50%", width: Math.max(2, size * 1.6), height: Math.max(2, size * 1.6), borderRadius: "50%", background: ink, transform: "translate(-50%, -50%)" }} />
        </span>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <p style={{ margin: 0, fontSize: "0.75rem", color: "var(--ion-color-medium)", display: "flex", alignItems: "center", gap: 4 }}>
          {!isEmpty && <><Check className="w-3 h-3 text-emerald-600" /> Signature captured</>}
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <IonButton color="primary" size="small" onClick={handleUndo} disabled={isEmpty}>
            <Undo2 className="w-4 h-4 mr-1" /> Undo
          </IonButton>
          <IonButton color="danger" size="small" onClick={handleClear} disabled={isEmpty}>
            <Trash2 className="w-4 h-4 mr-1" /> Clear
          </IonButton>
        </div>
      </div>
    </div>
  );
}
